import { useCallback, useState } from 'react';
import { Download, Image as ImageIcon, Palette, Share2, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { toast } from 'sonner';
import { useLanguage } from '@/i18n/LanguageContext';
import { lc } from '@/i18n/copy';
import { cn } from '@/lib/utils';
import { getShareText } from '@/lib/shareText';
import { motion, AnimatePresence } from 'framer-motion';

const copy = {
  en: {
    download: 'Download Share Card',
    generating: 'Generating...',
    success: 'Share card downloaded!',
    fail: 'Could not generate share card.',
    customize: 'Customize Preview Image',
    style: 'Style',
    preview: 'Preview',
    shareDirectly: 'Share directly',
    excerpt: 'Shows a short excerpt — not the full message',
  },
  es: {
    download: 'Descargar Tarjeta',
    generating: 'Generando...',
    success: '¡Tarjeta descargada!',
    fail: 'No se pudo generar la tarjeta.',
    customize: 'Personalizar Imagen',
    style: 'Estilo',
    preview: 'Vista previa',
    shareDirectly: 'Compartir directamente',
    excerpt: 'Muestra un extracto corto, no el mensaje completo',
  },
  fr: {
    download: 'Télécharger la Carte',
    generating: 'Génération...',
    success: 'Carte téléchargée !',
    fail: 'Impossible de générer la carte.',
    customize: 'Personnaliser l\'Image',
    style: 'Style',
    preview: 'Aperçu',
    shareDirectly: 'Partager directement',
    excerpt: 'Affiche un court extrait, pas le message complet',
  },
  pt: {
    download: 'Baixar Cartão',
    generating: 'Gerando...',
    success: 'Cartão baixado!',
    fail: 'Não foi possível gerar o cartão.',
    customize: 'Personalizar Imagem',
    style: 'Estilo',
    preview: 'Visualização',
    shareDirectly: 'Compartilhar diretamente',
    excerpt: 'Mostra um trecho curto, não a mensagem completa',
  },
  ar: {
    download: 'تحميل البطاقة',
    generating: 'جارٍ الإنشاء...',
    success: 'تم تحميل البطاقة!',
    fail: 'تعذر إنشاء البطاقة.',
    customize: 'تخصيص الصورة',
    style: 'النمط',
    preview: 'معاينة',
    shareDirectly: 'مشاركة مباشرة',
    excerpt: 'يعرض مقتطفاً قصيراً — ليس الرسالة الكاملة',
  },
  ja: {
    download: 'シェアカードをダウンロード',
    generating: '生成中...',
    success: 'シェアカードをダウンロードしました！',
    fail: 'シェアカードを生成できませんでした。',
    customize: 'プレビュー画像をカスタマイズ',
    style: 'スタイル',
    preview: 'プレビュー',
    shareDirectly: '直接共有',
    excerpt: '短い抜粋を表示します — 全文ではありません',
  },
  ko: {
    download: '공유 카드 다운로드',
    generating: '생성 중...',
    success: '공유 카드가 다운로드되었습니다!',
    fail: '공유 카드를 생성할 수 없습니다.',
    customize: '미리보기 이미지 맞춤설정',
    style: '스타일',
    preview: '미리보기',
    shareDirectly: '직접 공유',
    excerpt: '짧은 발췌만 표시됩니다 — 전체 메시지가 아닙니다',
  },
  id: {
    download: 'Unduh Kartu Berbagi',
    generating: 'Membuat...',
    success: 'Kartu berhasil diunduh!',
    fail: 'Tidak dapat membuat kartu.',
    customize: 'Sesuaikan Gambar',
    style: 'Gaya',
    preview: 'Pratinjau',
    shareDirectly: 'Bagikan langsung',
    excerpt: 'Menampilkan kutipan singkat — bukan pesan lengkap',
  },
  hi: {
    download: 'शेयर कार्ड डाउनलोड करें',
    generating: 'उत्पन्न हो रहा है...',
    success: 'शेयर कार्ड डाउनलोड हो गया!',
    fail: 'शेयर कार्ड उत्पन्न नहीं हो सका।',
    customize: 'पूर्वावलोकन छवि अनुकूलित करें',
    style: 'शैली',
    preview: 'पूर्वावलोकन',
    shareDirectly: 'सीधे साझा करें',
    excerpt: 'एक संक्षिप्त अंश दिखाता है — पूरा संदेश नहीं',
  },
};

// ─── Card style presets ───
interface CardStyle {
  label: string;
  bgGradient: [string, string, string];
  quoteColor: string;
  textColor: string;
  accentColor: string;
  brandColor: string;
  subtitleColor: string;
}

const CARD_STYLES: Record<string, CardStyle> = {
  midnight: {
    label: 'Midnight',
    bgGradient: ['#1a1625', '#2d1f3d', '#1a1625'],
    quoteColor: 'rgba(168, 85, 247, 0.3)',
    textColor: '#f0e6ff',
    accentColor: 'rgba(168, 85, 247, 0.6)',
    brandColor: 'rgba(168, 85, 247, 0.6)',
    subtitleColor: 'rgba(240, 230, 255, 0.7)',
  },
  warmth: {
    label: 'Warmth',
    bgGradient: ['#3d2b1f', '#5c3d2e', '#3d2b1f'],
    quoteColor: 'rgba(251, 191, 36, 0.3)',
    textColor: '#fef3c7',
    accentColor: 'rgba(251, 191, 36, 0.5)',
    brandColor: 'rgba(251, 191, 36, 0.6)',
    subtitleColor: 'rgba(254, 243, 199, 0.7)',
  },
  ocean: {
    label: 'Ocean',
    bgGradient: ['#0f172a', '#1e3a5f', '#0f172a'],
    quoteColor: 'rgba(56, 189, 248, 0.3)',
    textColor: '#e0f2fe',
    accentColor: 'rgba(56, 189, 248, 0.5)',
    brandColor: 'rgba(56, 189, 248, 0.6)',
    subtitleColor: 'rgba(224, 242, 254, 0.7)',
  },
  rose: {
    label: 'Rose',
    bgGradient: ['#2d1a2e', '#4a1942', '#2d1a2e'],
    quoteColor: 'rgba(244, 114, 182, 0.3)',
    textColor: '#fce7f3',
    accentColor: 'rgba(244, 114, 182, 0.5)',
    brandColor: 'rgba(244, 114, 182, 0.6)',
    subtitleColor: 'rgba(252, 231, 243, 0.7)',
  },
  forest: {
    label: 'Forest',
    bgGradient: ['#1a2e1a', '#2d4a2d', '#1a2e1a'],
    quoteColor: 'rgba(74, 222, 128, 0.3)',
    textColor: '#dcfce7',
    accentColor: 'rgba(74, 222, 128, 0.5)',
    brandColor: 'rgba(74, 222, 128, 0.6)',
    subtitleColor: 'rgba(220, 252, 231, 0.7)',
  },
  ivory: {
    label: 'Ivory',
    bgGradient: ['#faf8f5', '#f5f0e8', '#faf8f5'],
    quoteColor: 'rgba(120, 80, 50, 0.2)',
    textColor: '#3d2b1f',
    accentColor: 'rgba(120, 80, 50, 0.4)',
    brandColor: 'rgba(120, 80, 50, 0.5)',
    subtitleColor: 'rgba(61, 43, 31, 0.6)',
  },
};

interface ShareCardGeneratorProps {
  message: string;
  recipientName: string;
  className?: string;
  template?: string;
  senderName?: string | null;
  relationship?: string;
}

export function ShareCardGenerator({ message, recipientName, className, template, senderName, relationship }: ShareCardGeneratorProps) {
  const { language } = useLanguage();
  const c = lc(copy, language);
  const shareText = getShareText(senderName, relationship);
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedStyle, setSelectedStyle] = useState<string>('midnight');
  const [showCustomize, setShowCustomize] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const currentStyle = CARD_STYLES[selectedStyle] || CARD_STYLES.midnight;

  const renderToCanvas = useCallback((style: CardStyle) => {
    const canvas = document.createElement('canvas');
    const W = 1080;
    const H = 1080;
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas not supported');

    // Background gradient
    const grad = ctx.createLinearGradient(0, 0, W, H);
    grad.addColorStop(0, style.bgGradient[0]);
    grad.addColorStop(0.5, style.bgGradient[1]);
    grad.addColorStop(1, style.bgGradient[2]);
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, W, H);

    // Subtle decorative circles
    ctx.beginPath();
    ctx.arc(W * 0.85, H * 0.15, 120, 0, Math.PI * 2);
    ctx.fillStyle = style.quoteColor.replace(/[\d.]+\)$/, '0.08)');
    ctx.fill();

    ctx.beginPath();
    ctx.arc(W * 0.12, H * 0.88, 80, 0, Math.PI * 2);
    ctx.fillStyle = style.quoteColor.replace(/[\d.]+\)$/, '0.05)');
    ctx.fill();

    // Opening quote
    ctx.font = 'bold 80px Georgia, serif';
    ctx.fillStyle = style.quoteColor;
    ctx.textAlign = 'left';
    ctx.fillText('"', 80, 180);

    // Message excerpt
    const excerpt = message.length > 280 ? message.slice(0, 277) + '…' : message;
    ctx.font = '32px Georgia, serif';
    ctx.fillStyle = style.textColor;
    ctx.textAlign = 'left';
    const lines = wrapText(ctx, excerpt, W - 180);
    const maxLines = 10;
    const displayLines = lines.slice(0, maxLines);
    if (lines.length > maxLines) displayLines[maxLines - 1] += '…';

    let y = 240;
    for (const line of displayLines) {
      ctx.fillText(line, 90, y);
      y += 44;
    }

    // Closing quote
    ctx.font = 'bold 80px Georgia, serif';
    ctx.fillStyle = style.quoteColor;
    ctx.textAlign = 'right';
    ctx.fillText('"', W - 80, y + 40);

    // Divider line
    const dividerY = H - 220;
    ctx.beginPath();
    ctx.moveTo(90, dividerY);
    ctx.lineTo(W - 90, dividerY);
    ctx.strokeStyle = style.accentColor.replace(/[\d.]+\)$/, '0.2)');
    ctx.lineWidth = 1;
    ctx.stroke();

    // "For [name]"
    ctx.font = 'italic 28px Georgia, serif';
    ctx.fillStyle = style.subtitleColor;
    ctx.textAlign = 'left';
    ctx.fillText(`For ${recipientName}`, 90, dividerY + 50);

    // Branding
    ctx.font = 'bold 24px -apple-system, sans-serif';
    ctx.fillStyle = style.brandColor;
    ctx.textAlign = 'right';
    ctx.fillText('Say It NOW 💌', W - 90, H - 60);

    ctx.font = '16px -apple-system, sans-serif';
    ctx.fillStyle = style.subtitleColor.replace(/[\d.]+\)$/, '0.4)');
    ctx.fillText('say-it-now.app', W - 90, H - 30);

    return canvas;
  }, [message, recipientName]);

  const generatePreview = useCallback(() => {
    try {
      const canvas = renderToCanvas(currentStyle);
      setPreviewUrl(canvas.toDataURL('image/png', 0.5));
    } catch {
      // silently fail preview
    }
  }, [renderToCanvas, currentStyle]);

  const handleDownload = useCallback(async () => {
    setIsGenerating(true);
    try {
      const canvas = renderToCanvas(currentStyle);
      const link = document.createElement('a');
      link.download = `sayitnow-for-${recipientName.toLowerCase().replace(/\s+/g, '-')}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast.success(c.success);
    } catch {
      toast.error(c.fail);
    } finally {
      setIsGenerating(false);
    }
  }, [renderToCanvas, currentStyle, recipientName, c]);

  const handleNativeShare = useCallback(async () => {
    try {
      const canvas = renderToCanvas(currentStyle);
      canvas.toBlob(async (blob) => {
        if (!blob) {
          toast.error(c.fail);
          return;
        }
        const file = new File([blob], `sayitnow-for-${recipientName.toLowerCase().replace(/\s+/g, '-')}.png`, { type: 'image/png' });

        if (navigator.share && navigator.canShare?.({ files: [file] })) {
          await navigator.share({
            files: [file],
            title: `A message for ${recipientName}`,
            text: shareText,
          });
        } else {
          // Fallback to download
          await handleDownload();
        }
      }, 'image/png');
    } catch (err) {
      if ((err as Error).name !== 'AbortError') {
        toast.error(c.fail);
      }
    }
  }, [renderToCanvas, currentStyle, recipientName, c, handleDownload]);

  return (
    <div className={cn('space-y-3', className)}>
      <Collapsible open={showCustomize} onOpenChange={(open) => {
        setShowCustomize(open);
        if (open) generatePreview();
      }}>
        <CollapsibleTrigger asChild>
          <Button variant="ghost" className="w-full justify-between gap-2 text-sm text-muted-foreground hover:text-foreground">
            <span className="flex items-center gap-2">
              <Palette className="h-4 w-4" />
              {c.customize}
            </span>
            <Eye className="h-4 w-4" />
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="space-y-3 pt-3">
          {/* Style selector */}
          <div className="space-y-2">
            <Label className="text-xs">{c.style}</Label>
            <div className="grid grid-cols-3 gap-2">
              {Object.entries(CARD_STYLES).map(([key, s]) => (
                <button
                  key={key}
                  onClick={() => {
                    setSelectedStyle(key);
                    // Regenerate preview after state update
                    setTimeout(() => {
                      try {
                        const canvas = renderToCanvas(s);
                        setPreviewUrl(canvas.toDataURL('image/png', 0.5));
                      } catch {}
                    }, 10);
                  }}
                  className={cn(
                    'relative h-10 rounded-lg border-2 transition-all overflow-hidden',
                    selectedStyle === key
                      ? 'border-primary ring-2 ring-primary/20'
                      : 'border-border/50 hover:border-primary/40'
                  )}
                >
                  <div
                    className="absolute inset-0"
                    style={{
                      background: `linear-gradient(135deg, ${s.bgGradient[0]}, ${s.bgGradient[1]}, ${s.bgGradient[2]})`,
                    }}
                  />
                  <span
                    className="relative z-10 text-[10px] font-medium"
                    style={{ color: s.textColor }}
                  >
                    {s.label}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Preview thumbnail */}
          <AnimatePresence>
            {previewUrl && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="rounded-xl overflow-hidden border border-border/30 shadow-soft"
              >
                <img
                  src={previewUrl}
                  alt="Share card preview"
                  className="w-full aspect-square object-cover"
                />
              </motion.div>
            )}
          </AnimatePresence>

          {/* Action buttons */}
          <div className="grid grid-cols-2 gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownload}
              disabled={isGenerating}
              className="gap-1.5 text-xs"
            >
              <Download className="h-3.5 w-3.5" />
              {isGenerating ? c.generating : c.download}
            </Button>

            {typeof navigator !== 'undefined' && 'share' in navigator && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleNativeShare}
                className="gap-1.5 text-xs"
              >
                <Share2 className="h-3.5 w-3.5" />
                {c.shareDirectly}
              </Button>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Quick download button when customizer is closed */}
      {!showCustomize && (
        <Button
          variant="outline"
          size="sm"
          onClick={handleDownload}
          disabled={isGenerating}
          className="w-full"
        >
          {isGenerating ? (
            <>{c.generating}</>
          ) : (
            <>
              <ImageIcon className="h-4 w-4 mr-2" />
              {c.download}
            </>
          )}
        </Button>
      )}
      <p className="text-[11px] text-muted-foreground/60 text-center">{c.excerpt}</p>
    </div>
  );
}

/** Word-wrap text to fit within maxWidth */
function wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const words = text.split(' ');
  const lines: string[] = [];
  let currentLine = '';

  for (const word of words) {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    const metrics = ctx.measureText(testLine);
    if (metrics.width > maxWidth && currentLine) {
      lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = testLine;
    }
  }
  if (currentLine) lines.push(currentLine);
  return lines;
}
